import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react-lite';
import { useArticle, useKeywords } from '@app/hooks';
import CkContent from '@components/CkContent';
import ImageCarousel from '@components/ImageCarousel';
import { resolveStrapiImgUrl, convertOembedToIframe } from '@app/utils';
import { useStores } from '@stores';
import {
  ImageContainer,
  HeroPicture,
  HeroImage,
  Content,
  Title,
  Author,
  ClickableAuthor,
  AuthorLine,
  Date as DateComponent,
  NewsContent,
} from './styles';
import AuthorOverlay from '@components/AuthorOverlay';
import { useHidableNavElement } from '@app/hooks/useHidableNav';
import BookmarkLinks from '@components/BookmarkLinks';
import { formatDateTo0Timezone } from '@app/utils';

import Wrapper from '@components/Wrapper';
import { PAGE_PADDING_TOP } from '@constants';

const ArticlePage = () => {
  const [bookmarkSaved, setBookmarkSaved] = useState(false);
  const [authorDetailsOpen, setAuthorDetailsOpen] = useState(false);
  const {
    contentStore: { eventData },
    bookmarksStore: { setBookmark },
  } = useStores();

  const {
    loading,
    articleData,
    groupSlug,
    eventSlug,
    groupId,
    author,
    anyAuthorDetails,
  } = useArticle(eventData);
  const [imgRef, setImgRef] = useState<HTMLDivElement | null>(null);
  useHidableNavElement(imgRef, {
    rootMargin: `-${PAGE_PADDING_TOP}px 0px 0px 0px`,
  });

  useEffect(() => {
    if (articleData && groupSlug && eventSlug && !bookmarkSaved) {
      if (!articleData.disableBookmarking) {
        setBookmark({
          article: {
            slug: articleData?.slug,
            groupSlug,
            eventSlug,
            groupId,
          },
          type: 'article',
        });
      }

      setBookmarkSaved(true);
    }
  }, [articleData, groupSlug, eventSlug, bookmarkSaved]);

  const desktopHeroImage = resolveStrapiImgUrl(
    articleData?.desktopImage || articleData?.image
  );
  const mobileHeroImage = resolveStrapiImgUrl(
    articleData?.mobileImage || articleData?.image
  );

  const keywords = useKeywords(articleData?.keywords || []);

  const { carouselPosition, carouselItems } = useMemo(
    () => ({
      carouselPosition: articleData?.carouselPosition,
      carouselItems: articleData?.image_carousel?.carouselItems,
    }),
    [articleData]
  );

  if (loading || !articleData) return null;

  return (
    <Wrapper>
      <Helmet>
        <meta name="Keywords" content={keywords} />
      </Helmet>
      {desktopHeroImage && (
        <ImageContainer ref={setImgRef}>
          <HeroPicture>
            {mobileHeroImage && mobileHeroImage !== desktopHeroImage && (
              <source media="(max-width: 767px)" srcSet={mobileHeroImage} />
            )}
            <HeroImage
              src={desktopHeroImage}
              alt={articleData?.title || 'Article image'}
            />
          </HeroPicture>
        </ImageContainer>
      )}
      <Content>
        <Title>{articleData?.title}</Title>
        <div>
          <AuthorLine>
            by{' '}
            {anyAuthorDetails ? (
              <ClickableAuthor onClick={() => setAuthorDetailsOpen(true)}>
                {author?.name}
              </ClickableAuthor>
            ) : (
              <Author>{author?.name || articleData?.author}</Author>
            )}
          </AuthorLine>
          {articleData?.date && (
            <DateComponent>
              {formatDateTo0Timezone(articleData?.date, 'd MMM yyyy')}
            </DateComponent>
          )}
        </div>
        {carouselPosition === 'top' && <ImageCarousel images={carouselItems} />}
        <NewsContent>
          <CkContent
            text={convertOembedToIframe(articleData?.newsContent_ck)}
          />
        </NewsContent>
        {carouselPosition === 'bottom' && (
          <ImageCarousel images={carouselItems} />
        )}
        <BookmarkLinks bookmarkLinks={articleData?.bookmarkLinks} />
      </Content>
      {authorDetailsOpen && author && (
        <AuthorOverlay
          author={author}
          setAuthorDetailsOpen={setAuthorDetailsOpen}
        />
      )}
    </Wrapper>
  );
};

export default observer(ArticlePage);
